import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database,
  Globe,
  BarChart3,
  Sparkles,
  Layers,
  Zap,
  Radio,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import AgentWorkspacePanel from "./AgentWorkspacePanel";
import ResearchProgressTimeline from "./ResearchProgressTimeline";
import { AGENT_DISPLAY_CONFIG } from "../agentConfig";
import type { AgentId, AgentState, MultiAgentStreamState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ResearchWorkspaceProps {
  multiAgentState: MultiAgentStreamState;
  streaming: boolean;
  onAgentSelect?: (agentId: AgentId | "synthesis") => void;
  /** Compact mode for side panel */
  compact?: boolean;
}

type TabId = AgentId | "synthesis" | "overview";

// ─────────────────────────────────────────────────────────────────────────────
// Color Classes (static strings for Tailwind to detect)
// ─────────────────────────────────────────────────────────────────────────────

interface ColorClasses {
  bg: string;
  bgHover: string;
  border: string;
  borderHover: string;
  text: string;
  textMuted: string;
  dot: string;
  gradient: string;
}

const COLOR_CLASSES: Record<string, ColorClasses> = {
  emerald: {
    bg: "bg-emerald-500/20",
    bgHover: "group-hover:bg-emerald-500/30",
    border: "border-emerald-500/50",
    borderHover: "hover:border-emerald-500/40",
    text: "text-emerald-300",
    textMuted: "text-emerald-400",
    dot: "bg-emerald-400",
    gradient: "from-emerald-500 to-emerald-400",
  },
  cyan: {
    bg: "bg-cyan-500/20",
    bgHover: "group-hover:bg-cyan-500/30",
    border: "border-cyan-500/50",
    borderHover: "hover:border-cyan-500/40",
    text: "text-cyan-300",
    textMuted: "text-cyan-400",
    dot: "bg-cyan-400",
    gradient: "from-cyan-500 to-cyan-400",
  },
  amber: {
    bg: "bg-amber-500/20",
    bgHover: "group-hover:bg-amber-500/30",
    border: "border-amber-500/50",
    borderHover: "hover:border-amber-500/40",
    text: "text-amber-300",
    textMuted: "text-amber-400",
    dot: "bg-amber-400",
    gradient: "from-amber-500 to-amber-400",
  },
  violet: {
    bg: "bg-violet-500/20",
    bgHover: "group-hover:bg-violet-500/30",
    border: "border-violet-500/50",
    borderHover: "hover:border-violet-500/40",
    text: "text-violet-300",
    textMuted: "text-violet-400",
    dot: "bg-violet-400",
    gradient: "from-violet-500 to-violet-400",
  },
  fuchsia: {
    bg: "bg-fuchsia-500/20",
    bgHover: "group-hover:bg-fuchsia-500/30",
    border: "border-fuchsia-500/50",
    borderHover: "hover:border-fuchsia-500/40",
    text: "text-fuchsia-300",
    textMuted: "text-fuchsia-400",
    dot: "bg-fuchsia-400",
    gradient: "from-fuchsia-500 to-fuchsia-400",
  },
};

function getColorClasses(colorKey: string): ColorClasses {
  return COLOR_CLASSES[colorKey] ?? COLOR_CLASSES["violet"]!;
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent Icons (must be defined here as React components)
// ─────────────────────────────────────────────────────────────────────────────

const AGENT_ICONS: Record<AgentId, React.ReactNode> = {
  data: <Database className="h-4 w-4" />,
  search: <Globe className="h-4 w-4" />,
  chart: <BarChart3 className="h-4 w-4" />,
  synthesizer: <Sparkles className="h-4 w-4" />,
  quick_chat: <Sparkles className="h-4 w-4" />,
};

// Combined config using shared display config + icons
const agentConfig: Record<AgentId, { 
  icon: React.ReactNode; 
  colorKey: string;
  glowColor: string;
  label: string;
  description: string;
}> = Object.fromEntries(
  (Object.keys(AGENT_DISPLAY_CONFIG) as AgentId[]).map((id) => [
    id,
    {
      icon: AGENT_ICONS[id],
      colorKey: AGENT_DISPLAY_CONFIG[id]!.colorKey,
      glowColor: AGENT_DISPLAY_CONFIG[id]!.glowColor,
      label: AGENT_DISPLAY_CONFIG[id]!.label,
      description: AGENT_DISPLAY_CONFIG[id]!.description,
    },
  ])
) as Record<AgentId, { 
  icon: React.ReactNode; 
  colorKey: string;
  glowColor: string;
  label: string;
  description: string;
}>;

// ─────────────────────────────────────────────────────────────────────────────
// Tab Button
// ─────────────────────────────────────────────────────────────────────────────

const TabButton: React.FC<{
  id: TabId;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  status: AgentState["status"] | undefined;
  colorKey: string;
  glowColor: string;
  onClick: () => void;
  compact?: boolean;
}> = ({ label, icon, isActive, status, colorKey, glowColor, onClick, compact }) => {
  const isWorking = status === "working";
  const isDone = status === "done";
  const isError = status === "error";
  const colors = getColorClasses(colorKey);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative flex items-center gap-1.5 rounded-lg text-xs font-medium
        transition-all duration-300 border justify-center
        ${compact ? "px-2 py-1.5 min-w-0" : "px-4 py-2.5 min-w-[90px]"}
        ${isActive
          ? `${colors.bg} ${colors.border} ${colors.text}`
          : "bg-gray-900/40 border-gray-700/30 text-gray-400 hover:bg-gray-800/60 hover:text-gray-200"
        }
      `}
      style={{
        boxShadow: isActive && !compact ? `0 0 20px ${glowColor}30, inset 0 0 20px ${glowColor}10` : undefined,
      }}
    >
      {/* Animated ring for working state */}
      {isWorking && !compact && (
        <motion.div
          className="absolute inset-0 rounded-lg border-2"
          style={{ borderColor: glowColor }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0.3, 0.8, 0.3],
            scale: [1, 1.02, 1],
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}

      {/* Status indicator dot */}
      <span className={`
        ${compact ? "h-1.5 w-1.5" : "h-2 w-2"} rounded-full flex-shrink-0
        ${isWorking ? `${colors.dot} animate-pulse` : ""}
        ${isDone ? "bg-emerald-400" : ""}
        ${isError ? "bg-rose-400" : ""}
        ${!status || status === "pending" ? "bg-gray-600" : ""}
      `} />

      <span className={`${compact ? "h-3 w-3" : ""} ${isActive ? colors.text : ""}`}>{icon}</span>
      {!compact && <span className="hidden sm:inline whitespace-nowrap">{label}</span>}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Overview Panel
// ─────────────────────────────────────────────────────────────────────────────

const OverviewPanel: React.FC<{
  multiAgentState: MultiAgentStreamState;
  streaming: boolean;
  onAgentClick: (agentId: AgentId) => void;
}> = ({ multiAgentState, streaming, onAgentClick }) => {
  const { plannedAgents, agents } = multiAgentState;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center border border-violet-500/30">
          <Layers className="h-5 w-5 text-violet-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Research Overview</h3>
          <p className="text-xs text-gray-400">
            {streaming ? "Research in progress..." : "Research complete"}
          </p>
        </div>
      </div>

      {/* Progress Timeline */}
      <ResearchProgressTimeline 
        agents={plannedAgents.map(id => agents[id]).filter((a): a is AgentState => !!a)}
        streaming={streaming}
      />

      {/* Agent Grid */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        {plannedAgents.map((id) => {
          const agent = agents[id];
          if (!agent) return null;
          const config = agentConfig[id];
          const colors = getColorClasses(config.colorKey);
          const isWorking = agent.status === "working";
          const isDone = agent.status === "done";

          return (
            <motion.button
              key={id}
              type="button"
              onClick={() => onAgentClick(id)}
              className={`
                relative p-4 rounded-xl border text-left
                transition-all duration-300 group
                bg-gradient-to-br from-gray-900/60 to-gray-900/40
                ${isWorking 
                  ? colors.border 
                  : isDone 
                    ? "border-emerald-500/30" 
                    : "border-gray-700/40"
                }
                ${colors.borderHover} hover:bg-gray-800/40
              `}
              style={{
                boxShadow: isWorking ? `0 0 30px ${config.glowColor}20` : undefined,
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: plannedAgents.indexOf(id) * 0.1 }}
            >
              {/* Background grid pattern */}
              <div 
                className="absolute inset-0 opacity-[0.03] rounded-xl"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, ${config.glowColor} 1px, transparent 1px),
                    linear-gradient(to bottom, ${config.glowColor} 1px, transparent 1px)
                  `,
                  backgroundSize: "20px 20px",
                }}
              />

              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`
                    h-8 w-8 rounded-lg flex items-center justify-center
                    ${colors.bg} ${colors.textMuted}
                    ${colors.bgHover}
                    transition-colors
                  `}>
                    {config.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {agent.displayName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {agent.completedTools.length} operations
                    </div>
                  </div>
                </div>

                {/* Status bar */}
                <div className="mt-3 h-1 rounded-full bg-gray-800 overflow-hidden">
                  {isWorking ? (
                    <motion.div
                      className={`h-full bg-gradient-to-r ${colors.gradient}`}
                      initial={{ width: "10%" }}
                      animate={{ width: ["10%", "90%", "30%", "70%"] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  ) : isDone ? (
                    <div className="h-full w-full bg-emerald-500" />
                  ) : (
                    <div className="h-full w-0" />
                  )}
                </div>

                {/* Preview text */}
                {agent.tokenBuffer && (
                  <p className="mt-3 text-xs text-gray-400 line-clamp-2">
                    {agent.tokenBuffer.slice(-100)}...
                  </p>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ResearchWorkspace: React.FC<ResearchWorkspaceProps> = ({
  multiAgentState,
  streaming,
  compact = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { plannedAgents, agents, finalOutput } = multiAgentState;

  // Auto-collapse when final output arrives and streaming stops
  useEffect(() => {
    if (finalOutput && !streaming) {
      setIsCollapsed(true);
    }
  }, [finalOutput, streaming]);

  // Auto-switch to working agent
  useEffect(() => {
    if (streaming) {
      const workingAgent = plannedAgents.find(id => agents[id]?.status === "working");
      if (workingAgent && activeTab !== workingAgent && activeTab !== "overview") {
        // Don't auto-switch if user manually selected a tab
      }
    }
  }, [streaming, plannedAgents, agents, activeTab]);

  // Tab configuration including overview and synthesis
  const tabs = useMemo(() => {
    const tabList: Array<{
      id: TabId;
      label: string;
      icon: React.ReactNode;
      colorKey: string;
      glowColor: string;
      status: AgentState["status"] | undefined;
    }> = [
      {
        id: "overview",
        label: "Overview",
        icon: <Layers className="h-4 w-4" />,
        colorKey: "violet",
        glowColor: "rgb(139, 92, 246)",
        status: undefined,
      },
      ...plannedAgents.map((id) => ({
        id: id as TabId,
        label: agentConfig[id].label,
        icon: agentConfig[id].icon,
        colorKey: agentConfig[id].colorKey,
        glowColor: agentConfig[id].glowColor,
        status: agents[id]?.status,
      })),
    ];

    // Add synthesis tab if there's final output
    if (finalOutput) {
      tabList.push({
        id: "synthesis",
        label: "Report",
        icon: <Sparkles className="h-4 w-4" />,
        colorKey: "fuchsia",
        glowColor: "rgb(217, 70, 239)",
        status: undefined,
      });
    }

    return tabList;
  }, [plannedAgents, agents, finalOutput]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl border border-gray-700/40 bg-gradient-to-b from-gray-900/80 to-gray-900/60 backdrop-blur-xl overflow-hidden"
    >
      {/* Cyber grid background */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgb(139, 92, 246) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(139, 92, 246) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Glowing orb in corner */}
      <div 
        className="absolute -top-20 -right-20 h-40 w-40 rounded-full blur-3xl"
        style={{
          background: streaming 
            ? "radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)",
          transition: "background 0.5s ease",
        }}
      />

      {/* Header with live indicator and collapse toggle */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="relative w-full border-b border-gray-700/40 px-4 py-3 text-left hover:bg-gray-800/20 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center border border-violet-500/30">
              <Zap className="h-4 w-4 text-violet-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Deep Research</h2>
              <p className="text-xs text-gray-500">
                {isCollapsed 
                  ? `${plannedAgents.filter(id => agents[id]?.status === "done").length}/${plannedAgents.length} agents completed`
                  : "Multi-agent analysis workspace"
                }
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {streaming && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/30">
                <Radio className="h-3 w-3 text-violet-400 animate-pulse" />
                <span className="text-xs font-medium text-violet-300">Live</span>
              </div>
            )}
            <div className="h-6 w-6 rounded flex items-center justify-center text-gray-400 hover:text-white transition-colors">
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </div>
          </div>
        </div>
      </button>

      {/* Collapsible Content */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Tab Navigation */}
            <div className={`relative border-b border-gray-700/40 overflow-x-auto ${compact ? "px-2 py-1.5" : "px-4 py-2"}`}>
              <div className={`flex items-center ${compact ? "gap-1" : "gap-2"}`}>
                {tabs.map((tab) => (
                  <TabButton
                    key={tab.id}
                    id={tab.id}
                    label={tab.label}
                    icon={tab.icon}
                    isActive={activeTab === tab.id}
                    status={tab.status}
                    colorKey={tab.colorKey}
                    glowColor={tab.glowColor}
                    onClick={() => setActiveTab(tab.id)}
                    compact={compact}
                  />
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className={`relative overflow-y-auto ${compact ? "min-h-[200px] max-h-[350px] p-3" : "min-h-[400px] max-h-[600px] p-4"}`}>
              <AnimatePresence mode="wait">
                {activeTab === "overview" && (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <OverviewPanel
                      multiAgentState={multiAgentState}
                      streaming={streaming}
                      onAgentClick={(id) => setActiveTab(id)}
                    />
                  </motion.div>
                )}

                {plannedAgents.includes(activeTab as AgentId) && agents[activeTab as AgentId] && (
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <AgentWorkspacePanel
                      agent={agents[activeTab as AgentId]!}
                      config={agentConfig[activeTab as AgentId]}
                      streaming={streaming}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ResearchWorkspace;

